<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import RewardsIcon from "@apps/rewards/components/RewardsIcon.svelte";
    import RewardsPoints from "@apps/rewards/components/RewardsPoints.svelte";
    import { REWARDS_BRANDING_ICONS } from "@apps/rewards/constants/constants";
    import type RewardsAmountFormatter from "@apps/rewards/lib/RewardsAmountFormatter/RewardsAmountFormatter";
    import type RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
    import type { TranslationNamespaces } from "@apps/rewards/types";
    import { showPopUpOrDrawerWidget } from "@apps/rewards/utils/showPopUpOrDrawerWidget";
    import type RewardsPopUpWidgetLauncherManager from "@apps/rewards/widgets/rewards_popup_widget/utils/RewardsPopUpWidgetLauncherManager/RewardsPopUpWidgetLauncherManager";
    import type { ProductVariantIdManagerInterface } from "@lib/ProductVariantIdManager/ProductVariantIdManagerInterface";
    import type { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
    import type { Observable } from "rxjs";
    import {
        BehaviorSubject,
        combineLatestWith,
        lastValueFrom,
        map,
        tap,
    } from "rxjs";
    import { onMount } from "svelte";

    import Button from "@components/Buttons/Button.svelte";
    import type DrawerManager from "@components/Drawer/DrawerManager";
    import BaseSkeleton from "@components/skeletons/BaseSkeleton.svelte";
    import T from "@components/T.svelte";
    import Text from "@components/Typography/Text.svelte";

    import type RewardsEarnPointsQuery from "../../modules/EarnPoints/query/RewardsEarnPointsQuery";
    import type RewardsEarnPointsService from "../../modules/EarnPoints/service/RewardsEarnPointsService";

    export let earnPointsService: RewardsEarnPointsService;
    export let earnPointsQuery: RewardsEarnPointsQuery;
    export let storefrontValuesService: RewardsStorefrontValuesService;
    export let amountFormatter: RewardsAmountFormatter;
    export let launcherManager: RewardsPopUpWidgetLauncherManager;
    export let drawerManager: DrawerManager;
    export let productId: number;
    export let productVariantIdManager: ProductVariantIdManagerInterface;
    export let productQuantity$: Observable<string | null>;
    export let namespace: TranslationNamespaces;
    export let pointsNamespace: TranslationNamespaces;
    export let i18n: GwI18n;
    export let afterWidgetInject: () => void;

    const defaultQuantity = 1;

    const productQuantityStore$ = new BehaviorSubject<number>(defaultQuantity);
    const status$ = earnPointsQuery.status$;
    const branding$ = storefrontValuesService.selectRewardsBranding();

    const amountForProduct$ = earnPointsQuery.amountForProduct$.pipe(
        combineLatestWith(productVariantIdManager.variantId$),
        map(([{ variants, pointsAmount, storeCreditAmount }, variantId]) => {
            const variant = variants.find((v) => v.variantId === variantId);

            return {
                points: variant?.pointsAmount ?? pointsAmount,
                storeCredits: variant?.storeCreditAmount ?? storeCreditAmount,
            };
        }),
        tap(() => productQuantityStore$.next(defaultQuantity)),
        combineLatestWith(productQuantityStore$),
        map(([amounts, quantity]) => ({
            points: amounts.points * quantity,
            storeCredits: amounts.storeCredits * quantity,
        }))
    );

    onMount(() => {
        lastValueFrom(earnPointsService.getAmountForProduct(productId));
        afterWidgetInject();

        const productQuantitySubscription = productQuantity$
            .pipe(map((quantity) => +(quantity ?? defaultQuantity)))
            .subscribe(productQuantityStore$);

        return () => productQuantitySubscription.unsubscribe();
    });
</script>

{#if $branding$}
    <div class="gw-rd-earn-points-product-page-widget">
        {#if $status$ === STATUS.LOADING}
            <BaseSkeleton width="100%" height="30px" />
        {:else if $amountForProduct$.points || $amountForProduct$.storeCredits}
            <div class="gw-rd-earn-points-product-page-widget__content">
                <div
                    class="gw-rd-earn-points-product-page-widget__image-wrapper"
                >
                    <RewardsIcon
                        size="medium"
                        viewBox="0 0 24 24"
                        isBrandingIconColor={false}
                    >
                        <svelte:component
                            this={REWARDS_BRANDING_ICONS[
                                $branding$.popUpWidget.launcher.icon.type
                            ]}
                        />
                    </RewardsIcon>
                </div>

                <div>
                    {#if $amountForProduct$.points}
                        <RewardsPoints
                            let:value={{ points, pointsName }}
                            points={$amountForProduct$.points}
                            {amountFormatter}
                            {i18n}
                            {pointsNamespace}
                        >
                            <Text component="span" variant="base">
                                <T
                                    {namespace}
                                    translationKey="earn_points_on_purchase"
                                    translationOptions={{
                                        points,
                                        points_name: pointsName,
                                    }}
                                />
                            </Text>
                        </RewardsPoints>
                    {:else if $amountForProduct$.storeCredits}
                        <Text component="span" variant="base">
                            <T
                                {namespace}
                                translationKey="earn_store_credits_on_purchase"
                                translationOptions={{
                                    store_credits:
                                        $amountForProduct$.storeCredits,
                                }}
                            />
                        </Text>
                    {/if}

                    <Button
                        variant="link"
                        on:click={() =>
                            showPopUpOrDrawerWidget({
                                widgetType: $branding$.general.widget.type,
                                launcherManager,
                                drawerManager,
                            })}
                    >
                        <Text component="span" variant="base">
                            <T {namespace} translationKey="learn_more" />
                        </Text>
                    </Button>
                </div>
            </div>
        {/if}
    </div>
{/if}

<style lang="postcss">
    .gw-rd-earn-points-product-page-widget {
        &__content {
            display: flex;
            align-items: flex-start;
            column-gap: var(--gw-rd-space-4);
            border-radius: var(--gw-rd-border-radius-xs);
            padding: var(--gw-rd-space-5);
            font-size: 16px;
            background-color: var(--gw-background-color);
            color: var(--gw-font-color);
        }

        &__image-wrapper {
            display: var(--gw-icon-display);
            color: var(--gw-icon-color);
            align-items: center;
        }

        :global(.gw-btn.gw-button.--link) {
            font-size: 16px;
            color: var(--gw-font-color);
            text-decoration-color: var(--gw-font-color);
        }
    }
</style>
